import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Popup from "./Popup";
import "./ui/ui.css";
import "./tracks.css";

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : "");

/**
 * Display of a media track list (subtitles, audio tracks) with the upload of
 * a new one: language, file, then submit. The upload itself is done by the
 * parent (`onSubmit`); `tracks` are `{ _id, language }`.
 */
const TrackUploader = ({
  id,
  chooseLabel,
  languages,
  language,
  onLanguageChange,
  formOpen,
  accept,
  extensions,
  file,
  onFileChange,
  inputRef,
  showError,
  disabled,
  disableSubmit,
  disableCancel,
  onSubmit,
  onCancel,
  tracks,
  onDelete,
  deleteQuestion,
}) => {
  const { t } = useTranslation();
  const [toDelete, setToDelete] = useState(null);

  return (
    <div className="tracks">
      <select
        className="ui-input"
        aria-label={chooseLabel}
        value={language}
        onChange={onLanguageChange}
        disabled={disabled}
      >
        <option disabled value={`default${id}`}>
          {chooseLabel}
        </option>
        <optgroup label={t("Choose Language")}>
          {languages.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </optgroup>
      </select>

      {formOpen && (
        <div className="tracks-form">
          <label htmlFor={`${id}-file`} className="tracks-drop" data-disabled={disabled || undefined}>
            <i className="ri-upload-2-line" aria-hidden="true"></i>
            <span>
              <strong>{file ? file.name : t("Choose a file")}</strong>
              <small>
                {t("Supported format")} : {extensions}
              </small>
            </span>
          </label>
          <input
            id={`${id}-file`}
            type="file"
            accept={accept}
            className="sr-only"
            onChange={onFileChange}
            ref={inputRef}
            disabled={disabled}
          />
          {showError && (
            <p className="tracks-error" role="alert">
              <i className="ri-error-warning-line" aria-hidden="true"></i>
              {t("File Type Not Supported!")}
            </p>
          )}
          <div className="tracks-actions">
            <button
              type="button"
              className="ui-btn ui-btn-secondary"
              disabled={disableCancel}
              onClick={onCancel}
            >
              {t("cancel")}
            </button>
            <button type="button" className="ui-btn" disabled={disableSubmit} onClick={onSubmit}>
              {disableSubmit && disableCancel && (
                <span className="ui-spinner" aria-hidden="true" />
              )}
              {t("submit")}
            </button>
          </div>
        </div>
      )}

      {tracks?.length > 0 && (
        <ul className="tracks-list">
          {tracks.map((track) => (
            <li key={track?._id}>
              <i className="ri-translate-2" aria-hidden="true"></i>
              <span>{capitalize(track?.language)}</span>
              <button
                type="button"
                className="ui-icon-btn"
                data-tone="danger"
                aria-label={t("Delete")}
                title={t("Delete")}
                onClick={() => setToDelete(track)}
              >
                <i className="ri-delete-bin-line" aria-hidden="true"></i>
              </button>
            </li>
          ))}
        </ul>
      )}

      {toDelete && (
        <Popup
          open={true}
          setOpen={() => setToDelete(null)}
          deleteHandle={() => onDelete(toDelete._id)}
        >
          <div className="model-wrapper">
            {deleteQuestion(capitalize(toDelete.language))}
          </div>
        </Popup>
      )}
    </div>
  );
};

export default TrackUploader;
